import React, { useEffect, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import LabeledTextArea from "../../Widgets/LabeledTextArea";

export interface FamilyMentalHealthHistoryFormValues {
  familyMember: string;
  relationship: string;
  diagnoses: string;
  currentSupportServices: string;
  pastSupportServices: string;
}

const INITIAL_FORM: FamilyMentalHealthHistoryFormValues = {
  familyMember: "",
  relationship: "",
  diagnoses: "",
  currentSupportServices: "",
  pastSupportServices: "",
};

interface FamilyMentalHealthHistoryModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (values: FamilyMentalHealthHistoryFormValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<FamilyMentalHealthHistoryFormValues>;
}

export const FamilyMentalHealthHistoryModal: React.FC<FamilyMentalHealthHistoryModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
}) => {
  const [formValues, setFormValues] = useState<FamilyMentalHealthHistoryFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
    }
  }, [open]);

  const handleChange =
    (field: keyof FamilyMentalHealthHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (onSave) {
      onSave(formValues);
    } else {
      onClose();
    }
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={
        isEditMode ? "Edit Family Mental Health History" : "Add New Family Mental Health History"
      }
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <LabeledTextArea
            label="Family Member Name"
            value={formValues.familyMember}
            onChange={handleChange("familyMember")}
            multiline={false}
          />
          <LabeledTextArea
            label="Relation to Client"
            value={formValues.relationship}
            onChange={handleChange("relationship")}
            multiline={false}
          />
          <LabeledTextArea
            label="Diagnoses"
            value={formValues.diagnoses}
            onChange={handleChange("diagnoses")}
            multiline={false}
          />
          <LabeledTextArea
            label="Current Support Services"
            value={formValues.currentSupportServices}
            onChange={handleChange("currentSupportServices")}
            multiline={false}
          />
          <LabeledTextArea
            label="Past Support Services"
            value={formValues.pastSupportServices}
            onChange={handleChange("pastSupportServices")}
            multiline={false}
          />
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" onClick={handleSave} loading={isSaving}>
            {isEditMode ? "Save Changes" : "Add"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default FamilyMentalHealthHistoryModal;
